/**
 * Loading-state placeholder for the modern OddsPanel. Mirrors StatsSummary's
 * box model exactly — same `px-5 py-4`, same `gap-2`, same per-row heights —
 * so the sidebar/page never jumps when the real `odds` payload resolves and
 * only the three stats swap from shimmer bars to real numbers.
 *
 * Deliberately NOT a fixed `h-[…]`: the height it used to hardcode (96px)
 * had drifted 16px below the real block (112px), which is exactly the jump
 * this component exists to prevent. Deriving it from the same padding + line
 * heights keeps the two in sync when either side is retuned.
 */
export function OddsPanelSkeleton() {
  const barBase = 'rounded-[6px] bg-white/10 animate-pulse';

  return (
    <section
      aria-hidden
      aria-label="Pack odds (loading)"
      className="
        relative text-white
        @[800px]:overflow-hidden
        @[800px]:rounded-[28px]
        @[800px]:backdrop-blur-[27px]
        @[800px]:shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
        @[800px]:[background-image:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(153,153,153,0.2)_100%),linear-gradient(90deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.18)_100%)]
      "
    >
      <div className="flex flex-col gap-2 px-5 py-4">
        {/* Top row — dividers kept visible so the panel reads as the same
            component before data lands. `h-[22px]` mirrors the real "Max
            Pull" label's `leading-[22px]`. */}
        <div className="flex h-[22px] items-center gap-3">
          <span className="h-[2px] flex-1 rounded-[30px] bg-[#d9d9d9] opacity-20" />
          <div className={`h-[16px] w-[56px] ${barBase}`} />
          <span className="h-[2px] flex-1 rounded-[30px] bg-[#d9d9d9] opacity-20" />
        </div>

        {/* Bottom row — Min Value | hero | View Odds. Bar heights mirror the
            real line heights (label 20px + gap 2px + value 28px = 50px on the
            sides, 40px hero), so the row measures the same 50px. */}
        <div className="flex items-end gap-3">
          <div className="flex flex-1 flex-col items-start gap-[2px]">
            <div className={`h-[20px] w-[60px] ${barBase}`} />
            <div className={`h-[28px] w-[44px] ${barBase}`} />
          </div>

          <div className={`h-[40px] w-[88px] shrink-0 ${barBase}`} />

          <div className="flex flex-1 flex-col items-end gap-[2px]">
            <div className={`h-[20px] w-[70px] ${barBase}`} />
            <div className={`h-[28px] w-[52px] ${barBase}`} />
          </div>
        </div>
      </div>
    </section>
  );
}
